import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import PreviewRenderer from '../components/PreviewRenderer';
import PublicTenantLandingPage from './PublicTenantLandingPage';
import CookieBanner from '../components/CookieBanner';
import PagePopupModal from '../components/PagePopupModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('nexus_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('nexus_session_id', sessionId);
  }
  return sessionId;
};

// Initialize Google Analytics
const initGoogleAnalytics = (measurementId) => {
  if (!measurementId || window.gtag) return;
  
  // Add gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
};

// Track page view with client-side deduplication
const trackedPages = new Set();
const DEDUP_TIMEOUT = 5000; // Don't re-track same page within 5 seconds

const trackPageView = async (siteSlug, data) => {
  try {
    // Create a unique key for this page view
    const pageKey = `${siteSlug}:${data.page_id || data.article_id || data.page_slug || 'landing'}`;
    
    // Check if we recently tracked this page
    if (trackedPages.has(pageKey)) {
      console.debug('Skipping duplicate tracking for:', pageKey);
      return;
    }
    
    // Mark as tracked and set timeout to allow re-tracking later
    trackedPages.add(pageKey);
    setTimeout(() => trackedPages.delete(pageKey), DEDUP_TIMEOUT);
    
    await fetch(`${API_URL}/api/public/track/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Subdomain': siteSlug
      },
      body: JSON.stringify({
        ...data,
        session_id: getSessionId(),
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        screen_width: window.screen?.width,
        screen_height: window.screen?.height,
        language: navigator.language || navigator.userLanguage,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    });
  } catch (e) {
    // Silently fail - tracking shouldn't break the page
    console.debug('Tracking failed:', e);
  }
};

/**
 * PublicSitePage - Renders a tenant's public site via path-based routing
 * URL pattern: /site/:siteSlug
 * This is the primary multi-tenant routing method for Emergent hosting
 */
const PublicSitePage = () => {
  const { siteSlug, pageSlug, articleSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [siteData, setSiteData] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  // Apply the tenant's favicon as soon as site data loads.
  // PublicSitePage renders tenant sites on custom domains (e.g. schoolsuite.com.au)
  // so BrandingContext (which only runs for admin views) never fires here.
  useEffect(() => {
    const favUrl = siteData?.branding?.favicon_url;
    if (!favUrl) return;
    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = favUrl;
    });
    return () => {
      // Reset to platform default on unmount
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const link = document.querySelector(`link[rel="${rel}"]`);
        if (link) link.href = `${process.env.PUBLIC_URL || ''}/favicon.png`;
      });
    };
  }, [siteData?.branding?.favicon_url]);

  // Get language from URL parameter
  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam) {
      setCurrentLanguage(langParam);
      // Save to localStorage for persistent navigation
      localStorage.setItem('nexus_language', langParam);
    } else {
      // Check localStorage for previously set language (both keys for consistency
      // with the LanguageSelector component's own storage key).
      const savedLang = localStorage.getItem('nexus_language')
        || localStorage.getItem('nexuscms_language_preference');
      if (savedLang) {
        setCurrentLanguage(savedLang);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (articleSlug) {
          // Fetch specific article
          const res = await fetch(`${API_URL}/api/public/site/${siteSlug}/article/${articleSlug}`);
          if (!res.ok) {
            if (res.status === 404) throw new Error('Article not found');
            throw new Error('Failed to load article');
          }
          const data = await res.json();
          setArticleData(data);
          setSiteData({ tenant_name: data.tenant_name, theme: data.theme, menus: data.menus, branding: data.branding });
          
          // Track article view
          trackPageView(siteSlug, {
            article_id: data.article?.article_id,
            page_slug: articleSlug,
            page_type: 'article'
          });
        } else if (pageSlug) {
          // Fetch specific page
          const res = await fetch(`${API_URL}/api/public/site/${siteSlug}/page/${pageSlug}`);
          if (!res.ok) {
            if (res.status === 404) throw new Error('Page not found');
            throw new Error('Failed to load page');
          }
          const data = await res.json();
          setPageData(data);
          setSiteData({ tenant_name: data.tenant_name, theme: data.theme, menus: data.menus, branding: data.branding });
          
          // Track page view
          trackPageView(siteSlug, {
            page_id: data.page?.page_id,
            page_slug: pageSlug,
            page_type: 'page'
          });
        } else {
          // Fetch main site/landing page
          const res = await fetch(`${API_URL}/api/public/site/${siteSlug}`);
          if (!res.ok) {
            if (res.status === 404) throw new Error('Site not found');
            throw new Error('Failed to load site');
          }
          const data = await res.json();
          setSiteData(data);
          
          // Track landing page view
          trackPageView(siteSlug, {
            page_type: 'landing'
          });
        }
      } catch (err) {
        console.error('Error loading site data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (siteSlug) {
      fetchData();
    }
  }, [siteSlug, pageSlug, articleSlug]);

  // Fetch translation when language changes
  useEffect(() => {
    const fetchTranslation = async () => {
      const branding = siteData?.branding || {};
      const defaultLanguage = branding.default_language || 'en';
      const translationEnabled = branding.translation_enabled;
      
      // Only translate if: translation enabled, language is set, and it's different from default
      if (!translationEnabled || !currentLanguage || currentLanguage === defaultLanguage) {
        setTranslatedContent(null);
        return;
      }
      
      // For articles, translate the article blocks
      if (articleData?.article?.blocks) {
        setIsTranslating(true);
        try {
          const response = await fetch(
            `${API_URL}/api/translation/translate-blocks`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                blocks: articleData.article.blocks,
                target_language: currentLanguage,
                tenant_id: articleData.article.tenant_id
              })
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            setTranslatedContent(data.translated_blocks);
          }
        } catch (err) {
          console.error('Translation failed:', err);
        } finally {
          setIsTranslating(false);
        }
      }
      
      // For pages, translate page blocks
      if (pageData?.page?.blocks) {
        setIsTranslating(true);
        try {
          const response = await fetch(
            `${API_URL}/api/translation/translate-blocks`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                blocks: pageData.page.blocks,
                target_language: currentLanguage,
                tenant_id: pageData.page.tenant_id
              })
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            setTranslatedContent(data.translated_blocks);
          }
        } catch (err) {
          console.error('Translation failed:', err);
        } finally {
          setIsTranslating(false);
        }
      }
    };
    
    fetchTranslation();
  }, [currentLanguage, articleData, pageData, siteData]);

  // Initialize Google Analytics if configured
  useEffect(() => {
    if (siteData?.branding?.ga_measurement_id) {
      initGoogleAnalytics(siteData.branding.ga_measurement_id);
    }
  }, [siteData?.branding?.ga_measurement_id]);

  // Control DynatexSaaS badge visibility based on branding settings
  // IMPORTANT: This hook must be before any early returns to follow React's rules of hooks
  const branding = siteData?.branding || {};
  const reseller_branding = siteData?.reseller_branding || pageData?.reseller_branding || articleData?.reseller_branding;
  
  useEffect(() => {
    const badge = document.getElementById('dynatex-badge');

    // If tenant belongs to a reseller, rebrand the badge BEFORE revealing it
    if (badge && reseller_branding) {
      badge.href = reseller_branding.website_url || '#';
      const text = badge.querySelector('span');
      if (text) {
        text.textContent = `Made by ${reseller_branding.company_name}`;
        text.style.color = reseller_branding.primary_color || '#4F46E5';
      }
    }

    // Only reveal the badge if branding settings explicitly allow it
    if (branding && branding.show_platform_branding !== false) {
      document.body.classList.add('show-dynatex-badge');
    } else {
      document.body.classList.remove('show-dynatex-badge');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('show-dynatex-badge');
      // Reset badge to default
      const badge = document.getElementById('dynatex-badge');
      if (badge) {
        badge.href = 'https://www.dynatexsaas.com/';
        const text = badge.querySelector('span');
        if (text) {
          text.textContent = 'Made by DynatexSaaS';
          text.style.color = '#374151';
        }
      }
    };
  }, [branding, reseller_branding]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading site...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {error === 'Site not found' ? '404' : 'Error'}
          </h1>
          <p className="text-xl text-slate-600 mb-2">{error}</p>
          <p className="text-slate-500 mb-6">
            {error === 'Site not found' 
              ? "The site you're looking for doesn't exist or has been removed."
              : "Something went wrong while loading this site."
            }
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get theme and landing page data
  const theme = siteData?.theme || {};
  const landingPage = siteData?.landing_page;
  const pages = siteData?.pages || [];
  const menus = siteData?.menus || [];
  // Menu visibility: `is_global` menus show on every page; otherwise the
  // menu only shows on pages whose id is in its `page_ids` array. If both
  // types are configured for the same location, the page-specific one
  // wins as it's the more explicit intent. Prior to Aug 2026 this file
  // grabbed "the first menu at this location" unconditionally, which
  // made unchecking a page in the Navigation Manager have no effect.
  const currentPageId = pageData?.page?.page_id || articleData?.article?.article_id || null;
  const pickMenu = (location) => {
    const candidates = menus.filter(m => m.location === location);
    const pageSpecific = candidates.find(m => currentPageId && m.page_ids?.includes(currentPageId));
    if (pageSpecific) return pageSpecific;
    return candidates.find(m => m.is_global) || null;
  };
  const headerMenu = pickMenu('header');
  const footerMenu = pickMenu('footer');

  // Render article
  if (articleData) {
    const article = articleData.article;
    // Use translated blocks if available, otherwise original blocks, fallback to content HTML
    const blocksToRender = translatedContent || article.blocks;
    const hasBlocks = blocksToRender && blocksToRender.length > 0;
    const navMode = article.article_nav_mode || (article.show_site_navigation === false ? 'none' : 'site_nav');
    
    const articleContent = (
      <article className="max-w-4xl mx-auto px-4 py-12 bg-white rounded-lg my-8 shadow-sm"
        style={{ color: theme.bodyColor || '#334155' }}
      >
        {article.featured_image && (
          <figure className="mb-3">
            <img 
              src={article.featured_image} 
              alt={article.title}
              className="w-full rounded-xl"
              style={{ maxHeight: '520px', objectFit: 'contain' }}
            />
            {article.image_caption && (
              <figcaption className="text-xs text-slate-500 mt-2 text-center italic">
                {article.image_caption}
              </figcaption>
            )}
          </figure>
        )}
        
        <h1 className="text-4xl font-bold mb-4" style={{ color: theme.headingColor || '#1e293b' }}>
          {article.title}
        </h1>
        
        {article.published_at && (
          <p className="text-slate-500 mb-8">
            {new Date(article.published_at).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </p>
        )}
        
        {isTranslating && (
          <div className="flex items-center gap-2 text-slate-500 mb-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Translating...</span>
          </div>
        )}
        
        {hasBlocks ? (
          <PreviewRenderer 
            blocks={blocksToRender}
            theme={theme}
            siteTheme={theme}
            subdomain={siteSlug}
            pages={pages}
          />
        ) : (
          <div 
            className="prose prose-lg max-w-none"
            style={{ color: theme.bodyColor || '#334155' }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        )}
      </article>
    );

    if (navMode === 'site_nav') {
      return (
        <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor || '#ffffff' }}>
          <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
            <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
              <Link to={`/site/${siteSlug}`} className="flex items-center gap-2">
                {headerMenu?.logo?.url && (
                  <img src={headerMenu.logo.url} alt="" className="h-8" />
                )}
                <span className="font-semibold text-lg">{siteData?.tenant_name}</span>
              </Link>
              {currentLanguage && currentLanguage !== (branding.default_language || 'en') && (
                <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">
                  {currentLanguage.toUpperCase()}
                </span>
              )}
            </div>
          </header>
          {articleContent}
          {branding.show_platform_branding && (
            <div className="text-center py-4 bg-slate-100 text-slate-500 text-sm">
              Powered by <a href="https://dynatexsaas.com" className="text-indigo-600 hover:underline">DynatexSaaS</a>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor || '#ffffff' }}>
        {navMode === 'back_link' && (
          <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
            <div className="max-w-4xl mx-auto px-4 py-3">
              <Link 
                to={`/site/${siteSlug}`}
                className="inline-flex items-center text-sm font-medium hover:underline"
                style={{ color: theme.accentColor || '#4f46e5' }}
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back to {siteData?.tenant_name || 'Site'}
              </Link>
            </div>
          </div>
        )}
        {articleContent}
        {branding.show_platform_branding && (
          <div className="text-center py-4 bg-slate-100 text-slate-500 text-sm">
            Powered by <a href="https://dynatexsaas.com" className="text-indigo-600 hover:underline">DynatexSaaS</a>
          </div>
        )}
      </div>
    );
  }

  // Render specific page
  if (pageData) {
    const page = pageData.page;
    // Use translated blocks if available
    const blocksToRender = translatedContent || page.blocks || [];
    
    return (
      <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor || '#ffffff' }}>
        {/* Header */}
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to={`/site/${siteSlug}`} className="flex items-center gap-2">
              {headerMenu?.logo?.url && (
                <img src={headerMenu.logo.url} alt="" className="h-8" />
              )}
              <span className="font-semibold text-lg">{siteData?.tenant_name}</span>
            </Link>
            {currentLanguage && currentLanguage !== (branding.default_language || 'en') && (
              <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">
                {currentLanguage.toUpperCase()}
              </span>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: theme.headingColor || '#1e293b' }}>
            {page.title}
          </h1>
          
          {isTranslating && (
            <div className="flex items-center gap-2 text-slate-500 mb-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Translating...</span>
            </div>
          )}
          
          <PreviewRenderer 
            blocks={blocksToRender}
            theme={page.theme || theme}
            siteTheme={page.theme || theme}
            tenantId={siteData?.tenant_id || pageData?.page?.tenant_id || articleData?.article?.tenant_id}
            subdomain={siteSlug}
            pages={pages}
          />
        </main>

        {/* Branding Footer */}
        {branding.show_platform_branding && (
          <div className="text-center py-4 bg-slate-100 text-slate-500 text-sm">
            Powered by <a href="https://dynatexsaas.com" className="text-indigo-600 hover:underline">DynatexSaaS</a>
          </div>
        )}
        <PagePopupModal
          tenantId={siteData?.tenant_id || pageData?.page?.tenant_id || articleData?.article?.tenant_id}
          pageId={pageData?.page?.page_id || null}
        />
      </div>
    );
  }

  // Render main landing page using PublicTenantLandingPage for consistent styling
  // This ensures the preview matches the custom domain rendering
  if (!pageSlug && !articleSlug && siteData?.landing_page) {
    return (
      <PublicTenantLandingPage 
        tenantData={{
          ...siteData,
          landing_page: siteData.landing_page
        }}
        subdomain={siteSlug}
        isSubdomainMode={true}
      />
    );
  }

  const landingBlocks = landingPage?.blocks || [];
  const articles = siteData?.recent_articles || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor || '#ffffff' }}>
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {headerMenu?.logo?.url && (
                <a
                  href={headerMenu.logo.link || '/'}
                  className="flex items-center hover:opacity-80 transition-opacity"
                  aria-label="Go to homepage"
                  data-testid="header-logo-link"
                >
                  <img 
                    src={headerMenu.logo.url} 
                    alt={headerMenu.logo.alt || siteData?.tenant_name || 'Logo'}
                    style={{ height: `${headerMenu.logo.height || 40}px` }}
                    className="object-contain"
                  />
                </a>
              )}
              <span className="font-semibold text-xl" style={{ color: theme.headingColor || '#1e293b' }}>
                {siteData?.tenant_name}
              </span>
            </div>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {headerMenu?.items?.map(item => (
                <a
                  key={item.item_id}
                  href={item.url || '#'}
                  className="text-sm font-medium hover:text-indigo-600 transition-colors"
                  style={{ color: theme.bodyColor || '#475569' }}
                >
                  {item.label}
                </a>
              ))}
              {pages.slice(0, 5).map(page => (
                <Link
                  key={page.page_id}
                  to={`/site/${siteSlug}/page/${page.slug}`}
                  className="text-sm font-medium hover:text-indigo-600 transition-colors"
                  style={{ color: theme.bodyColor || '#475569' }}
                >
                  {page.title}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {landingBlocks.length > 0 ? (
          <PreviewRenderer 
            blocks={landingBlocks}
            theme={landingPage?.theme || theme}
            siteTheme={landingPage?.theme || theme}
            tenantId={siteData?.tenant_id}
            subdomain={siteSlug}
            pages={pages}
          />
        ) : (
          /* Default landing content if no blocks */
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: theme.headingColor || '#1e293b' }}
            >
              Welcome to {siteData?.tenant_name}
            </h1>
            <p 
              className="text-xl mb-8"
              style={{ color: theme.bodyColor || '#64748b' }}
            >
              This site is being built. Check back soon!
            </p>
          </div>
        )}

        {/* Recent Articles Section */}
        {articles.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 py-16">
            <h2 
              className="text-2xl font-bold mb-8"
              style={{ color: theme.headingColor || '#1e293b' }}
            >
              Latest News
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => (
                <Link
                  key={article.article_id}
                  to={`/site/${siteSlug}/article/${article.slug || article.article_id}`}
                  className="group bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
                >
                  {article.featured_image && (
                    <img 
                      src={article.featured_image} 
                      alt={article.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <h3 
                      className="font-semibold mb-2 group-hover:text-indigo-600 transition-colors"
                      style={{ color: theme.headingColor || '#1e293b' }}
                    >
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p 
                        className="text-sm line-clamp-2"
                        style={{ color: theme.bodyColor || '#64748b' }}
                      >
                        {article.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer 
        className="border-t mt-auto"
        style={{ backgroundColor: footerMenu?.footer_settings?.backgroundColor || '#f8fafc' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Logo & Name */}
            <div>
              {footerMenu?.logo?.url && (
                <img 
                  src={footerMenu.logo.url} 
                  alt={siteData?.tenant_name}
                  className="h-10 mb-4"
                />
              )}
              <p className="text-sm text-slate-600">
                {siteData?.tenant_name}
              </p>
            </div>

            {/* Quick Links */}
            {pages.length > 0 && (
              <div>
                <h4 className="font-semibold mb-4 text-slate-900">Quick Links</h4>
                <ul className="space-y-2">
                  {pages.slice(0, 5).map(page => (
                    <li key={page.page_id}>
                      <Link
                        to={`/site/${siteSlug}/page/${page.slug}`}
                        className="text-sm text-slate-600 hover:text-indigo-600"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer Links */}
            {footerMenu?.items?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-4 text-slate-900">Links</h4>
                <ul className="space-y-2">
                  {footerMenu.items.map(item => (
                    <li key={item.item_id}>
                      <a
                        href={item.url || '#'}
                        className="text-sm text-slate-600 hover:text-indigo-600"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Copyright & Branding */}
          <div className="border-t mt-8 pt-8 text-center">
            <p className="text-sm text-slate-500">
              {footerMenu?.footer_settings?.copyright_text || `© ${new Date().getFullYear()} ${siteData?.tenant_name}. All rights reserved.`}
            </p>
            {branding.show_platform_branding && (
              <p className="text-xs text-slate-400 mt-2">
                Powered by <a href="https://dynatexsaas.com" className="text-indigo-500 hover:underline">DynatexSaaS</a>
              </p>
            )}
          </div>
        </div>
      </footer>
      <CookieBanner settings={siteData?.branding?.cookie_consent} />
      <PagePopupModal
        tenantId={siteData?.tenant_id}
        pageId={pageData?.page?.page_id || null}
      />
    </div>
  );
};

export default PublicSitePage;
